/**
 * Sync API client — HTTP wrapper around the Umbra V2 sync endpoints.
 *
 * Wraps the shared `requestApi` helper so all calls inherit auth headers,
 * token refresh, and error handling from the central Axios instance.
 *
 * Uses endpoint constants from `apiEndpoints.ts` so all URLs resolve
 * against `VITE_UMBRA_API_URL` — the same base used by every other
 * Umbra endpoint.
 */

import { requestApi } from "@/server/apiService";
import { ApiEndpoints } from "@/server/apiEndpoints";
import type { ChatEntity } from "@/indexedDB/types";
import type { MessagesResponse } from "@/server/types/umbra/message";

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface ChatListResponse {
  chats: ChatEntity[];
  cursor: string | null;
  hasMore: boolean;
}

export interface DeltaSyncResponse {
  updatedChats: ChatEntity[];
  removedChatIds: string[];
  toCursor: string | null;
  fullSync: boolean;
}

export interface SyncEvent {
  type: string;
  messageId: string;
  data: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Raw backend event shape (from /umbra/v2/sync/events)
// ---------------------------------------------------------------------------

/**
 * Raw event object as returned by the backend. Each event is stored verbatim
 * by the server (Redis stream → Mongo) and returned without reshaping:
 *
 *   { eventType, action, chatId, payload, streamId, streamMs, streamSeq, ... }
 *
 * The frontend sync engine consumes the flat `SyncEvent` contract instead, so
 * `fetchEvents` normalizes every raw event into that shape (see
 * `normalizeRawEvent`). We model only the fields the normalizer reads.
 *
 * Observed shapes (staging, 2026-06-09):
 *   - Message edit:   eventType="Message",       action="Update",
 *                     payload.id=<msgId>, payload.content=<ciphertext>,
 *                     payload.is_edited=true
 *   - Message delete: eventType="Message",       action="Delete",
 *                     payload.id=<msgId>
 *   - Reaction add:   eventType="ReactionEvent",  action="Create",
 *                     payload.message_id=<msgId>, payload.reaction=<emoji>,
 *                     payload.summary={ [emoji]: count }
 *   - Reaction remove:eventType="ReactionEvent",  action="Delete", (same payload)
 *   - ResetChatCounter / other: dropped.
 *   - Message(Create) is already filtered out server-side.
 */
interface RawSyncEvent {
  eventType?: string;
  action?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: Record<string, any>;
}

/**
 * Map a single raw backend event to the flat `SyncEvent` contract the sync
 * engine's `applyEvent` consumes. Returns `null` for events that should be
 * dropped (unknown types, ResetChatCounter, or events missing the fields we
 * need). Never throws — a malformed event is skipped, not fatal.
 */
function normalizeRawEvent(raw: RawSyncEvent): SyncEvent | null {
  const payload = raw?.payload;
  if (!payload) {
    return null;
  }

  switch (raw.eventType) {
    case "Message": {
      // Message edits/deletes carry the message id in `payload.id`.
      const messageId = payload.id;
      if (typeof messageId !== "string" || messageId.length === 0) {
        return null;
      }
      if (raw.action === "Update") {
        // `payload.content` is still encrypted ciphertext here. Forward it (plus
        // the encrypted attachment meta) so applyEvent can store it on the row's
        // ciphertext sidecar — the decryption-retry orchestrator then decrypts
        // the edit from LOCAL bytes instead of refetching it (P3-RESIDUAL).
        //
        // A real content edit sets `is_edited: true`. A metadata-only update
        // (e.g. a chunked upload's "uploading"→"complete"/"failed" status patch)
        // sets `is_edited: false` — the text is UNCHANGED. Routing the latter
        // through `message.edited` would wrongly blank the visible text and mark
        // the row decryption-failed (see applyEvent). Distinguish them so a
        // meta-only update refreshes attachment_meta WITHOUT clobbering content.
        //
        // CONTRACT INVARIANT (FE↔BE): this relies on the BE setting is_edited
        // truthfully — true for content edits, false for metadata-only patches.
        // A genuine content edit emitted with is_edited:false would be misrouted
        // (stale text retained); a meta patch with is_edited:true would blank the
        // text. Both emitters are FE-owned today (editMessage → true;
        // patchUploadStatus → false). A `undefined`/missing flag falls through to
        // the content-clearing `message.edited` (the safe default for unknowns).
        return {
          type:
            payload.is_edited === false
              ? "message.metaUpdate"
              : "message.edited",
          messageId,
          data: {
            content: payload.content,
            attachmentMeta: payload.attachment_meta ?? null,
          },
        };
      }
      if (raw.action === "Delete") {
        return { type: "message.deleted", messageId, data: {} };
      }
      // Message/Create is filtered server-side; any other action is dropped.
      return null;
    }

    case "ReactionEvent": {
      // Reactions carry the message id in `payload.message_id` and the full
      // post-change count map in `payload.summary` (Record<emoji, count>).
      // The backend only emits `Create` / `Delete`; any other action is dropped
      // rather than defaulted to an add (so a malformed/unknown action can't add
      // a phantom reaction).
      if (raw.action !== "Create" && raw.action !== "Delete") {
        return null;
      }
      const messageId = payload.message_id;
      const emoji = payload.reaction;
      if (
        typeof messageId !== "string" ||
        messageId.length === 0 ||
        typeof emoji !== "string" ||
        emoji.length === 0
      ) {
        return null;
      }
      // Forward the FULL post-change summary so the engine can replace the local
      // `reactions` object wholesale — mirroring the WS path
      // (reactionEventsHandler sets `reactions: summary` for both add and
      // remove). A per-emoji merge would leave stale emojis and, on a full
      // removal, a phantom `{ [emoji]: undefined }` key.
      //
      // Because the engine REPLACES the whole map, a missing/malformed summary
      // is dangerous: defaulting it to `{}` would wipe every reaction on the
      // message. The real BE always sends `summary` (an explicit `{}` means the
      // last reaction was removed and is honoured), so treat absent/non-object
      // summaries as malformed and DROP the event rather than clobbering state.
      if (
        payload.summary == null ||
        typeof payload.summary !== "object" ||
        Array.isArray(payload.summary)
      ) {
        return null;
      }
      const summary = payload.summary as Record<string, number>;
      const type =
        raw.action === "Delete" ? "reaction.removed" : "reaction.added";
      return { type, messageId, data: { summary } };
    }

    default:
      // ResetChatCounter and any other event type — not reconcilable here.
      return null;
  }
}

export interface ChatEventsEntry {
  events: SyncEvent[];
  cursor: string | null;
  hasMore: boolean;
}

export interface UpdatedThread {
  chatId: string;
  // The backend `/sync/events` handler returns the thread/parent message id
  // under `parentId` (see eventsHandler.getEventsForChats — it builds
  // `{ chatId, parentId: threadId, commentCount }`), NOT `messageId`. The sync
  // engine maps `parentId` onto the local message row that owns the thread.
  parentId: string;
  commentCount: number;
}

export interface EventsResponse {
  eventsByChat: Record<string, ChatEventsEntry>;
  updatedThreads: UpdatedThread[];
}

/**
 * Raw `updatedThreads` entry as returned by the backend. The handler emits
 * `{ chatId, parentId, commentCount }`, but we read the parent id defensively
 * across a few historical field-name variants (`parentId`, then `threadId`,
 * then `messageId`) so a future BE rename can't silently break thread counts.
 */
interface RawUpdatedThread {
  chatId?: string;
  parentId?: string;
  threadId?: string;
  messageId?: string;
  commentCount?: number;
}

/**
 * Normalize raw `updatedThreads` entries into the flat `UpdatedThread` contract
 * the sync engine consumes. The parent/thread message id is read from
 * `parentId` (current BE), falling back to `threadId`/`messageId` for
 * resilience. Entries with no resolvable id are dropped here so the engine
 * never calls `findAndUpdateMessage(undefined)`.
 */
function normalizeUpdatedThreads(
  raw: RawUpdatedThread[] | undefined,
): UpdatedThread[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const result: UpdatedThread[] = [];
  for (const t of raw) {
    if (!t) {
      continue;
    }
    const parentId = t.parentId ?? t.threadId ?? t.messageId;
    if (typeof parentId !== "string" || parentId.length === 0) {
      continue;
    }
    result.push({
      chatId: t.chatId ?? "",
      parentId,
      commentCount: typeof t.commentCount === "number" ? t.commentCount : 0,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Fetch the full chat list (first-time sync or pagination).
 * GET /umbra/v2/chats
 *
 * `type` selects which chat collection to fetch. The default (group) list is
 * keyed by Member rows; P2P chats have no Member row and live in a separate
 * collection, so they must be requested explicitly with `type='p2p'`.
 */
export async function fetchChatList(
  cursor?: string,
  limit?: number,
  type?: "group" | "p2p",
): Promise<ChatListResponse> {
  const params: Record<string, unknown> = {};
  if (cursor) {
    params.cursor = cursor;
  }
  if (limit != null) {
    params.limit = limit;
  }
  if (type) {
    params.type = type;
  }

  const response = await requestApi(
    {
      method: "GET",
      url: ApiEndpoints.SYNC_CHAT_LIST,
      params,
    },
    { retry429: true },
  );

  return response.data;
}

/**
 * Delta sync — send current cursor + local chat IDs, receive changes.
 * POST /umbra/v2/sync/chats
 */
export async function fetchDeltaSync(
  cursor: string,
  localChatIds: string[],
): Promise<DeltaSyncResponse> {
  const response = await requestApi(
    {
      method: "POST",
      url: ApiEndpoints.SYNC_DELTA,
      data: { cursor, localChatIds },
    },
    { retry429: true },
  );

  return response.data;
}

/**
 * Fetch a single chat by ID.
 * GET /umbra/v2/chats/:chatId
 */
export async function fetchChatById(chatId: string): Promise<ChatEntity> {
  const response = await requestApi(
    {
      method: "GET",
      url: `${ApiEndpoints.SYNC_CHAT_LIST}/${chatId}`,
    },
    { retry429: true },
  );

  return response.data;
}

/**
 * Fetch new messages for a chat since the given message ID.
 * GET /umbra/v2/chats/:chatId/messages?after=lastMessageId
 *
 * Used by the sync engine to retrieve messages sent while the client was
 * offline. Returns the raw server response (encrypted messages + cursor).
 */
export async function fetchNewMessages(
  chatId: string,
  after?: string,
): Promise<MessagesResponse> {
  const params: Record<string, unknown> = {};
  if (after) {
    params.after = after;
  }

  const response = await requestApi(
    {
      method: "GET",
      url: `${ApiEndpoints.UMBRA_MESSAGES}/${chatId}/messages`,
      params,
    },
    { retry429: true },
  );

  return response.data;
}

/**
 * Fetch per-chat events (edits, deletes, reactions) for reconciliation.
 * POST /umbra/v2/sync/events
 */
export async function fetchEvents(
  chatIds: string[],
  since: string,
  until?: string,
): Promise<EventsResponse> {
  const data: Record<string, unknown> = { chatIds, since };
  if (until !== undefined) {
    data.until = until;
  }

  const response = await requestApi(
    {
      method: "POST",
      url: ApiEndpoints.SYNC_EVENTS,
      data,
    },
    { retry429: true },
  );

  // The backend returns each event in its raw, on-the-wire shape
  // (`{ eventType, action, payload, ... }`). The sync engine consumes the flat
  // `SyncEvent` contract (`{ type, messageId, data }`), so normalize every raw
  // event at this boundary. Unmappable events (ResetChatCounter, unknown types,
  // malformed payloads) are dropped here rather than silently swallowed deep in
  // applyEvent's `default` branch.
  const body = (response.data ?? {}) as {
    eventsByChat?: Record<
      string,
      { events?: RawSyncEvent[]; cursor?: string | null; hasMore?: boolean }
    >;
    updatedThreads?: RawUpdatedThread[];
  };

  const eventsByChat: Record<string, ChatEventsEntry> = {};
  for (const [chatId, entry] of Object.entries(body.eventsByChat ?? {})) {
    const rawEvents = Array.isArray(entry?.events) ? entry.events : [];
    const normalized: SyncEvent[] = [];
    for (const raw of rawEvents) {
      const mapped = normalizeRawEvent(raw);
      if (mapped) {
        normalized.push(mapped);
      }
    }
    eventsByChat[chatId] = {
      events: normalized,
      cursor: entry?.cursor ?? null,
      hasMore: entry?.hasMore ?? false,
    };
  }

  return {
    eventsByChat,
    updatedThreads: normalizeUpdatedThreads(body.updatedThreads),
  };
}
